/**
 * Surrogate Mode helpers — enter / exit / read state.
 *
 * How it works
 * ─────────────
 * Normal login stores the user's JWT under localStorage["desky_token"].
 * When the user enters Surrogate Mode we:
 *   1. Call POST /api/auth/start-surrogate-session with their normal
 *      token to get a NEW JWT that impersonates Teacher A.
 *   2. Save the current normal token under "desky_original_token".
 *   3. Overwrite "desky_token" with the surrogate JWT + write the
 *      metadata (subject, class, target teacher, end_date) under
 *      "desky_surrogate_meta" so the banner can render without an
 *      extra fetch.
 *
 * Exit is the reverse — restore the original token and clear the
 * surrogate keys. No blocklist / server call is required (server
 * revalidates the substitution on every impersonated request, so
 * revoke is enforced immediately).
 */

const API = process.env.REACT_APP_BACKEND_URL;

const KEY_TOKEN = 'desky_token';
const KEY_ORIG = 'desky_original_token';
const KEY_META = 'desky_surrogate_meta';

async function _readJson(res) {
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; }
  catch { return { detail: text || `HTTP ${res.status}` }; }
}

export function getSurrogateState() {
  const active = !!localStorage.getItem(KEY_ORIG);
  let meta = null;
  try {
    const raw = localStorage.getItem(KEY_META);
    meta = raw ? JSON.parse(raw) : null;
  } catch { meta = null; }
  return { active, meta };
}

export async function fetchAvailableSurrogates() {
  const token = localStorage.getItem(KEY_TOKEN);
  if (!token) return { surrogate_active: false, available: [] };
  const res = await fetch(`${API}/api/auth/surrogate/available`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return { surrogate_active: false, available: [] };
  return _readJson(res);
}

/** Re-validates the CURRENT token's surrogate claims against the live
 * substitution row (backend/auth_middleware.py's per-request resolver).
 * Returns `{ok: true, surrogate_active}` on success, `{ok: false, network: true}`
 * when offline / 5xx (keep the session), `{ok: false}` if the
 * request itself failed (401/403) — which happens the moment the session
 * is ended from another device, since `surrogate_session_active` gets
 * cleared server-side and every subsequent request re-checks it. */
export async function checkSurrogateSelf() {
  const token = localStorage.getItem(KEY_TOKEN);
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${API}/api/auth/surrogate/self`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Only a real 401/403 means the session ended; a 5xx is a blip to retry.
    if (!res.ok && res.status !== 401 && res.status !== 403) return { ok: false, network: true };
    const body = await _readJson(res);
    // `message` only for structured reasons (e.g. the covered teacher's
    // licence was revoked); plain-string details mean "ended elsewhere".
    if (!res.ok) return { ok: false, message: body.detail?.message || null };
    return { ok: true, surrogate_active: !!body.surrogate_active };
  } catch {
    return { ok: false, network: true };
  }
}

/** Substitute accepts an assignment (idempotent). */
export async function acceptSubstitution(substitutionId) {
  const token = localStorage.getItem(KEY_TOKEN);
  const res = await fetch(`${API}/api/substitutions/${substitutionId}/accept`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const body = await _readJson(res);
  if (!res.ok) throw new Error(body.detail || 'Failed to accept substitution');
  return body;
}

/** Enter Surrogate Mode. On success the caller should reload the app
 * (or refresh their user context) so every subsequent API call uses
 * the surrogate token. */
export async function enterSurrogateMode(substitutionId) {
  const token = localStorage.getItem(KEY_TOKEN);
  if (!token) throw new Error('Not logged in');
  const res = await fetch(`${API}/api/auth/start-surrogate-session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ substitution_id: substitutionId }),
  });
  const body = await _readJson(res);
  if (!res.ok) throw new Error(body.detail || 'Failed to start surrogate session');
  // Only overwrite the token AFTER the server confirmed — otherwise a
  // failure would strand the user without their normal token.
  localStorage.setItem(KEY_ORIG, token);
  localStorage.setItem(KEY_TOKEN, body.token);
  localStorage.setItem(KEY_META, JSON.stringify({
    substitution_id: body.substitution_id,
    surrogate_for: body.surrogate_for,
    surrogate_for_name: body.surrogate_for_name,
    actor_id: body.actor_id,
    actor_name: body.actor_name,
    scope: body.scope,
    subject_id: body.subject_id,
    subject_name: body.subject_name,
    class_id: body.class_id,
    class_name: body.class_name,
    end_date: body.end_date,
    entered_at: new Date().toISOString(),
  }));
  return body;
}

/** Exit Surrogate Mode — restore the original token. Best-effort
 * server-side audit log; even if that call fails we clear locally. */
export async function exitSurrogateMode() {
  const surrogateToken = localStorage.getItem(KEY_TOKEN);
  try {
    await fetch(`${API}/api/auth/end-surrogate-session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${surrogateToken}`, 'Content-Type': 'application/json' },
    });
  } catch { /* audit failure is not blocking */ }
  const orig = localStorage.getItem(KEY_ORIG);
  if (orig) localStorage.setItem(KEY_TOKEN, orig);
  localStorage.removeItem(KEY_ORIG);
  localStorage.removeItem(KEY_META);
  return true;
}
